import React, { Suspense, useMemo } from 'react';
import { Box, styled } from '@cherre-frontend/ui';
import OverviewDivider from '../../../../components/OverviewDivider';
import OverviewCard, { OverviewType } from './OverviewCard';
import ConnectorButton from './ConnectorButton';
import { buildValidationStatusOverviewCard } from './util/buildValidationStatusOverviewCard';
import { ValidationStatus } from '../../../../components/ValidationStatusPopover';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import OverviewNextStepsSection from './OverviewNextStepsSection';
import ConnectorStageBanner from './ConnectorStageBanner';
import { useGetOverviewProdReturn } from './hooks/useGetOverviewProd/model';
import { useLazyLoadQuery } from 'react-relay';
import { RulesQuery } from '../DataValidationV2/queries/types';
import { DataValidationRulesLibraryQuery } from '../DataValidationV2/queries/dataValidationRulesLibraryQuery';
import { useConnectorOverviewUI } from '../../contexts/ConnectorOverviewUI';
import DestinationEditConfigurationTables from './DestinationEditConfigurationTables';
import ConnectorRevisionReleaseBanner from './ConnectorRevisionReleaseBanner';
import DestinationEditConfigurationTablesSkeletons from './DestinationEditConfigurationTables/skeletons.index';
import { ConnectorStages } from 'src/products/connector/constants';
// It will be released soon
// import ReviewAndUpdateModelModal from '../ReviewAndUpdateModelModal';

// It will be released soon
// import ConnectorBannerUpdateAvailable from '../ConnectorBannerUpdateAvailable';
// import UpdateConnectorRevisionModal from '../UpdateConnectorRevisionModal';

const Container = styled('div')`
  display: grid;
  grid-template-columns: 1fr 46px 1fr 46px 1fr;
  gap: 32px;
`;

const NextStepsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: end;
  gap: 40px;
`;

type Props = {
  connector: useGetOverviewProdReturn['data']['connector'];
  stage: ConnectorStages;
};

const OverviewContentV1: React.FC<Props> = ({ connector, stage }) => {
  // It will be released soon
  // const [isOpen, setIsOpen] = useState<boolean>(false);
  const canAddConnector = useFeatureFlag('ConnectorsCreateConnector');
  const [connectorOverviewUI] = useConnectorOverviewUI();

  const modelStatus = useMemo<ValidationStatus>(
    () => buildValidationStatusOverviewCard(connector),
    [connector]
  );

  const status = modelStatus;
  const renderEditDestinationTables =
    connectorOverviewUI.connectorComponent === 'destinations' &&
    connectorOverviewUI.data?.action === 'edit_configuration';

  const renderRevisionBanner =
    connectorOverviewUI.connectorComponent === 'destinations' &&
    connectorOverviewUI.data?.renderRevisionBanner;

  // It will be released soon
  // const renderReviewAndUpdateModal =
  //   connectorOverviewUI.connectorComponent === 'model' &&
  //   connectorOverviewUI.data?.action === 'view_update';

  // It will be released soon
  // const handleGoToDev = useCallback(() => {
  //   setConnectorOverviewUI({ data: { stage: ConnectorStages.DEV } });
  // }, []);

  // const handleOnCloseUpdateModal = useCallback(() => {
  //   setIsOpen(false);
  // }, []);

  // const handleOnUpdateRevision = useCallback(() => {
  //   setIsOpen(true);
  // }, []);

  // const handleOnCloseReviewAndUpdateModal = useCallback(() => {
  //   setConnectorOverviewUI({
  //     connectorComponent: undefined,
  //     data: { ...connectorOverviewUI.data, action: undefined },
  //   });
  // }, [connectorOverviewUI]);

  return (
    <Box sx={{ width: '100%', paddingX: '32px' }}>
      <ConnectorStageBanner stage={stage} />
      {/* It will be released soon */}
      {/* {stage === ConnectorStages.QC && (
        <ConnectorBannerUpdateAvailable
          onClickGoDev={handleGoToDev}
          onClickUpdateConnector={handleOnUpdateRevision}
        />
      )} */}
      {renderRevisionBanner && <ConnectorRevisionReleaseBanner />}
      <>
        <Container>
          <OverviewCard
            data={connector?.sources || []}
            failed={connector?.failedConnectorComponent === OverviewType.SOURCE}
            type={OverviewType.SOURCE}
          />
          <OverviewDivider />
          <OverviewCard
            data={connector?.models || []}
            failed={connector?.failedConnectorComponent === OverviewType.MODEL}
            type={OverviewType.MODEL}
          />
          <OverviewDivider status={status} />
          <OverviewCard
            data={connector?.destinations || []}
            failed={
              connector?.failedConnectorComponent === OverviewType.DESTINATION
            }
            type={OverviewType.DESTINATION}
          />
        </Container>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            marginTop: '20px',
          }}
        >
          {!renderEditDestinationTables && (
            <NextStepsContainer>
              <OverviewNextStepsSection />

              {canAddConnector && (
                <ConnectorButton variant='contained' disabled>
                  Save Connection
                </ConnectorButton>
              )}
            </NextStepsContainer>
          )}
          {renderEditDestinationTables && (
            <Suspense
              fallback={<DestinationEditConfigurationTablesSkeletons />}
            >
              <DestinationEditConfigurationTables connector={connector} />
            </Suspense>
          )}
        </Box>
      </>
      {/* It will be released soon */}
      {/* {isOpen && (
        <UpdateConnectorRevisionModal
          name='Test'
          isOpen={isOpen}
          onClose={handleOnCloseUpdateModal}
          onUpdate={console.log}
        />
      )} */}
      {/* It will be released soon */}
      {/* {renderReviewAndUpdateModal && (
        <ReviewAndUpdateModelModal
          isOpen={renderReviewAndUpdateModal}
          onUpdate={console.log}
          onClose={handleOnCloseReviewAndUpdateModal}
          newestRevision='001'
          releaseDate='date'
          revisionsToUpdate={['001', '002', '003']}
          currentRevision={{
            updatedAt: '01/01/2020',
            createdAt: '10/02/2021',
            createdBy: 'Leo',
            revisionNumber: '002',
            updatedBy: 'Me',
            updatesMade:
              'Source availability changed from 11:30 PM to 11:55 PM ',
          }}
        />
      )} */}
    </Box>
  );
};

const OverviewContentV2: React.FC<Props> = ({ connector, stage }) => {
  const canAddConnector = useFeatureFlag('ConnectorsCreateConnector');

  const modelStatus = useMemo<ValidationStatus>(
    () => buildValidationStatusOverviewCard(connector),
    [connector]
  );

  let status = modelStatus;
  const result = useLazyLoadQuery<RulesQuery>(DataValidationRulesLibraryQuery, {
    connectorComponentRef: {
      name: connector.models[0]?.name,
      kind: 'Model',
      version: connector.models[0]?.revision,
    },
  });
  const rules = result.validationRulesLibrary?.filter(
    (rule) => rule.status !== 'INACTIVE'
  );
  if (rules?.some((rule) => rule.status === 'FAIL')) {
    status = ValidationStatus.FAILED;
  } else if (rules?.every((rule) => rule.status === 'SUCCESS')) {
    status = ValidationStatus.PASSED;
  } else {
    status = ValidationStatus.NOT_RUN;
  }

  return (
    <Box sx={{ width: '100%', paddingX: '32px' }}>
      <ConnectorStageBanner stage={stage} />
      <>
        <Container>
          <OverviewCard
            data={connector?.sources || []}
            failed={connector?.failedConnectorComponent === OverviewType.SOURCE}
            type={OverviewType.SOURCE}
          />
          <OverviewDivider />
          <OverviewCard
            data={connector?.models || []}
            failed={connector?.failedConnectorComponent === OverviewType.MODEL}
            type={OverviewType.MODEL}
          />
          <OverviewDivider status={status} />
          <OverviewCard
            data={connector?.destinations || []}
            failed={
              connector?.failedConnectorComponent === OverviewType.DESTINATION
            }
            type={OverviewType.DESTINATION}
          />
        </Container>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            marginTop: '20px',
          }}
        >
          <NextStepsContainer>
            <OverviewNextStepsSection />

            {canAddConnector && (
              <ConnectorButton variant='contained' disabled>
                Save Connection
              </ConnectorButton>
            )}
          </NextStepsContainer>
        </Box>
      </>
    </Box>
  );
};

class ErrorBoundary extends React.Component<
  Record<'children' | 'fallback', React.ReactNode>,
  { hasError: boolean }
> {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const OverviewContent: React.FC<Props> = (props) => {
  const isValidationV2 = useFeatureFlag('ConnectorsDataValidationV2');

  return (
    <ErrorBoundary fallback={<OverviewContentV1 {...props} />}>
      {isValidationV2 ? (
        <OverviewContentV2 {...props} />
      ) : (
        <OverviewContentV1 {...props} />
      )}
    </ErrorBoundary>
  );
};

export default OverviewContent;
